import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type {
  ConfirmIssuanceInput,
  ConfirmIssuanceResult,
  CreateApplicationInput,
  ObservePaymentInput,
  PolicyApplication,
  ResolveExceptionInput,
  RevalidateQuoteInput,
  TerminateApplicationInput,
} from "./types";

// The issuance-desk hooks (B5) — reads + the stage mutations over the shipped
// policy-application endpoints. Every mutation invalidates BOTH the detail and the queue
// (onSettled, so a boundary-raised exception also re-reads) — the surface then renders
// the fresh status/exception_type rather than branching on the mutation body.
const LIST_KEY = ["policy-applications"];
const appKey = (id: string | undefined) => ["policy-application", id];

/** GET /v1/policy-applications — the open-work queue (incl. exceptions). */
export function useApplications() {
  const api = useApi();
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: () => api.get<PolicyApplication[]>("/v1/policy-applications"),
  });
}

/** POST /v1/policy-applications (F2-F) — create an application from a DECIDED/ACCEPTED
 *  recommendation. The advisor triggers issuance (no auto-bind); on success the surface
 *  navigates to /issuance/:id where submit → observe-payment → confirm-issuance continues. */
export function useCreateApplication() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApplicationInput) =>
      api.post<{ id: string }>("/v1/policy-applications", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

/** GET /v1/policy-applications/:id — one application (scope-filtered). */
export function useApplication(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: appKey(id),
    queryFn: () => api.get<PolicyApplication>(`/v1/policy-applications/${id}`),
    enabled: !!id,
  });
}

function useStageMutation<TArgs>(
  id: string,
  run: (api: ReturnType<typeof useApi>, args: TArgs) => Promise<unknown>,
) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: TArgs) => run(api, args),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: appKey(id) });
      void qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

/** POST …/submit — DRAFT → SUBMITTED_TO_CARRIER. */
export function useSubmit(id: string) {
  return useStageMutation<void>(id, (api) => api.post(`/v1/policy-applications/${id}/submit`));
}

/** POST …/observe-payment — record the carrier-observed payment (no money movement). */
export function useObservePayment(id: string) {
  return useStageMutation<ObservePaymentInput>(id, (api, input) =>
    api.post(`/v1/policy-applications/${id}/observe-payment`, input),
  );
}

/** POST …/confirm-issuance — the price-integrity gate → ISSUED or ISSUANCE_EXCEPTION.
 *  An off-price `override` triggers the ApiClient's four-eyes flow automatically. */
export function useConfirmIssuance(id: string) {
  return useStageMutation<ConfirmIssuanceInput>(id, (api, input) =>
    api.post<ConfirmIssuanceResult>(`/v1/policy-applications/${id}/confirm-issuance`, input),
  );
}

/** POST …/resolve-exception — RETRY / FAIL a typed issuance exception. */
export function useResolveException(id: string) {
  return useStageMutation<ResolveExceptionInput>(id, (api, input) =>
    api.post(`/v1/policy-applications/${id}/resolve-exception`, input),
  );
}

// ── D16 — the three routes the console never called ──────────────────────────

/** POST …/revalidate-quote — attach a fresh accepted offer after the original quote
 *  expired. QUOTE_REVALIDATION-only (server-guarded); this is the ONLY way out of
 *  that state, and until now the console offered none. */
export function useRevalidateQuote(id: string) {
  return useStageMutation<RevalidateQuoteInput>(id, (api, input) =>
    api.post(`/v1/policy-applications/${id}/revalidate-quote`, input),
  );
}

/** POST …/withdraw — WE stop the application (terminal, pre-issuance). */
export function useWithdrawApplication(id: string) {
  return useStageMutation<TerminateApplicationInput>(id, (api, input) =>
    api.post(`/v1/policy-applications/${id}/withdraw`, input),
  );
}

/** POST …/record-declined — the CARRIER refused (terminal). Distinct from withdraw:
 *  the carrier-quality loop and the funnel read the difference. */
export function useRecordDeclined(id: string) {
  return useStageMutation<TerminateApplicationInput>(id, (api, input) =>
    api.post(`/v1/policy-applications/${id}/record-declined`, input),
  );
}
